import React, { useState, useEffect } from 'react';
import { Card, Badge, Button, Alert, Spinner } from 'react-bootstrap';
import { FaThumbsUp, FaThumbsDown } from 'react-icons/fa';
import ReadOnlyStars from './ReadOnlyStars';
import { Link } from 'react-router-dom';
import DeleteReviewButton from './DeleteReviewButton';

export default function ReviewList({ 
  spotifyId, 
  userId, 
  mode = 'item', // 'item' for album/track pages, 'user' for profile pages
  onReviewSubmitted,
  showItemInfo = true, // Whether to show item name/image (true for profile, false for item pages)
  currentUser = null, // Current logged-in user for comparison
  profileUsername = null, // Username of the profile being viewed (for other-user mode)
  newReview = null, // Newly submitted review to add immediately
  typeFilter = null, // 'track' | 'album' | null
  title = null      // override header title
}) {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [votingStates, setVotingStates] = useState({}); // Track voting state per review
  const [hoveredReview, setHoveredReview] = useState(null); // Track which review is being hovered

  useEffect(() => {
    fetchReviews();
  }, [spotifyId, userId, mode]);

  // Refresh reviews when a new review is submitted
  useEffect(() => {
    if (onReviewSubmitted) {
      fetchReviews();
    }
  }, [onReviewSubmitted]);

  // Add new review to the list immediately when it's submitted
  useEffect(() => {
    if (newReview) {
      setReviews(prevReviews => [newReview, ...prevReviews]);
    }
  }, [newReview]);


  // --- ID helpers (fixes /profile vs /user/:id routing) ---
  const getId = (x) => {
    if (!x) return null;
    if (typeof x === 'string') return x;
    return x._id || x.id || null;
  };

  const currentUserId =
  currentUser?._id || currentUser?.id || currentUser?.userId || null;

  // review.userId can be an object (populated) OR a string (id)
  const getReviewUserId = (review) => {
    const u = review?.userId;
    if (!u) return null;
    if (typeof u === 'string') return u;
    return u._id || u.id || null;
  };





  const fetchReviews = async () => {
    try {
      setLoading(true);
      setError('');

      let response;
      if (mode === 'user' || mode === 'other-user') {
        // Fetch user's reviews for profile page
        const token = localStorage.getItem('token');
        
        // If userId prop is provided, fetch that specific user's reviews
        if (userId) {
          response = await fetch(`/api/reviews/user/${userId}`);
        } else {
          // Fetch current user's reviews
          response = await fetch('/api/reviews/user/profile', {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
        }
        
        if (!response.ok) {
          throw new Error('Failed to load user reviews');
        }
      } else {
        // Fetch reviews for specific item (album/track)
        response = await fetch(`/api/reviews/${spotifyId}`);
        
        if (!response.ok) {
          throw new Error('Failed to load reviews');
        }
      }

      const data = await response.json();
      console.log(`Fetched ${data.length} reviews for ${mode === 'item' ? 'item' : 'user'}`);
      setReviews(data);
    } catch (err) {
      console.error('Error fetching reviews:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (reviewId, voteType) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/reviews/${reviewId}/${voteType}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        
        // Update the specific review's vote counts
        setReviews(prevReviews => 
          prevReviews.map(review => 
            review._id === reviewId 
              ? { ...review, upvotes: data.upvotes, downvotes: data.downvotes }
              : review
          )
        );

        // Update voting state to prevent multiple votes
        setVotingStates(prev => ({
          ...prev,
          [reviewId]: voteType
        }));
      } else {
        const errorData = await response.json();
        alert(errorData.message || 'Failed to vote');
      }
    } catch (err) {
      console.error('Error voting:', err);
      alert('Failed to vote. Please try again.');
    }
  };

  const handleDeleteReview = (reviewId) => {
    // Remove the deleted review from the state
    setReviews(prevReviews => prevReviews.filter(review => review._id !== reviewId));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getItemDisplay = (review) => {
    if (!showItemInfo) return null;

    const itemPath = `/${review.type}/${review.spotifyId}`;
    const isHovered = hoveredReview === review._id;

    return (
      <div 
        className="d-flex align-items-center mb-2"
        onMouseEnter={() => setHoveredReview(review._id)}
        onMouseLeave={() => setHoveredReview(null)}
      >
        {review.itemImage && (
          <Link to={itemPath} style={{ textDecoration: 'none' }}>
            <img 
              src={review.itemImage} 
              alt={review.itemName}
              style={{ 
                width: '40px', 
                height: '40px', 
                objectFit: 'cover',
                borderRadius: '4px',
                marginRight: '10px',
                cursor: 'pointer',
                transition: 'opacity 0.2s ease',
                opacity: isHovered ? '0.8' : '1'
              }}
            />
          </Link>
        )}
        <div>
          <Link 
            to={itemPath} 
            style={{ 
              textDecoration: 'none', 
              color: isHovered ? '#007bff' : 'inherit',
              fontWeight: 'bold',
              transition: 'color 0.2s ease'
            }}
          >
            {review.itemName}
          </Link>
          <Badge bg="secondary" className="ms-2">
            {review.type === 'album' ? 'Album' : 'Track'}
          </Badge>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <Card className="mb-4">
        <Card.Body className="text-center">
          <Spinner animation="border" role="status" size="sm">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <span className="ms-2">Loading reviews...</span>
        </Card.Body>
      </Card>
    );
  }

  // Apply type filter (for user profile columns)
  const displayedReviews = typeFilter
    ? reviews.filter(r => r?.type === typeFilter)
    : reviews;

  if (error) {
    return (
      <Card className="mb-4">
        <Card.Body>
          <Alert variant="danger">
            Error loading reviews: {error}
          </Alert>
        </Card.Body>
      </Card>
    );
  }

  if (displayedReviews.length === 0) {
    return (
      <Card className="mb-4">
        <Card.Body className="text-center py-4">
          {mode === 'user' ? (
            <>
              <p className="text-muted mb-0">
                {typeFilter === 'album'
                  ? 'You have no album reviews.'
                  : typeFilter === 'track'
                  ? 'You have no track reviews.'
                  : "You haven't written any reviews yet."}
              </p>
              <p className="text-muted">Start reviewing your favorite music!</p>
            </>
          ) : (
            <p className="text-muted mb-0">
              No reviews yet. Be the first to review!
            </p>
          )}
        </Card.Body>
      </Card>
    );
  }


  return (
    <Card className="mb-4">
      <Card.Header
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 3,
          background: '#fff',       // ensure the header isn’t transparent
          borderBottom: '1px solid #ddd'
        }}
      >
        <h5 className="mb-0">
          {title ?? (mode === 'user' ? 'My Reviews' : 'Reviews')}
          {' '}
          ({displayedReviews.length})
        </h5>
      </Card.Header>
      <Card.Body>
        <div className="d-flex flex-column gap-4">
          {displayedReviews.map((review) => (
            <div key={review._id} className="border-bottom pb-3 position-relative">
              {getItemDisplay(review)}
              
              <div className="d-flex align-items-center mb-2">
                <strong className="me-2">
                  {mode === 'user' ? (
                    'You'
                  ) : mode === 'other-user' ? (
                    profileUsername || 'Anonymous'
                  ) : review.userId?.username ? (
                    (() => {
                      const reviewUserId = getReviewUserId(review);
                      const isOwn =
                        currentUserId && reviewUserId && String(currentUserId) === String(reviewUserId);

                      const to = isOwn ? '/profile' : (reviewUserId ? `/user/${reviewUserId}` : '#');

                      return (
                        <Link
                          to={to}
                          style={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}
                          onMouseEnter={(e) => (e.target.style.color = '#007bff')}
                          onMouseLeave={(e) => (e.target.style.color = 'inherit')}
                        >
                          {review.userId.username}
                        </Link>
                      );
                    })()
                  ) : (
                    'Anonymous'
                  )}
                </strong>
                <div className="d-flex align-items-center me-2" style={{ color: '#333' }}>
                 <ReadOnlyStars
                  value={review.rating}
                  ariaLabel={`Rating ${review.rating} out of 5`}/>
                </div>
                <div className="ms-auto d-flex align-items-center">
                  <Badge bg="light" text="dark" className="me-2">
                    {formatDate(review.createdAt)}
                  </Badge>
                  {/* Delete button - only show for user's own reviews */}
                  {currentUser && (
                    (mode === 'user') || 
                    (mode === 'item' && String(getReviewUserId(review)) === String(currentUserId)) ||
                    (mode === 'other-user' && String(getReviewUserId(review)) === String(currentUserId))
                  ) && (
                    <DeleteReviewButton 
                      reviewId={review._id} 
                      onDelete={handleDeleteReview}
                    />
                  )}
                </div>
              </div>

              <p className="mb-2 review-snippet">
                {review.review}
              </p>

              {/* Voting section - only show for item pages, not user's own reviews */}
              {mode === 'item' && (
                <div className="d-flex align-items-center gap-3">
                  <Button
                    variant="outline-success"
                    size="sm"
                    onClick={() => handleVote(review._id, 'upvote')}
                    disabled={votingStates[review._id] === 'upvote'}
                  >
                    <FaThumbsUp className="me-1" />
                    {review.upvotes}
                  </Button>
                  
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => handleVote(review._id, 'downvote')}
                    disabled={votingStates[review._id] === 'downvote'}
                  >
                    <FaThumbsDown className="me-1" />
                    {review.downvotes}
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      </Card.Body>
    </Card>
  );
} 